import axios from 'src/utils/axios';
import helpers from 'src/helpers';
import { NOTIFICATION_STATUS } from 'src/constants';
import { addNotification } from 'src/actions/notificationActions';

// CON-1487 Phase G — Event Template Defaults Admin actions.
//
// Parallel (not overlapping) with eventTemplatesActions so the existing
// user-scoped CRUD pipeline stays byte-for-byte unchanged. Backend calls
// are all permission-gated (ADMIN_TOOLS_VIEW + EVENT_TEMPLATE_DEFAULTS_ADMIN).

// ── Action Types ──

export const GET_EVENT_TEMPLATE_ADMIN_SCOPES = '@event-template-admin/get-scopes';
export const GET_EVENT_TEMPLATE_ADMIN_SCOPES_SUCCESS = '@event-template-admin/get-scopes-success';
export const GET_EVENT_TEMPLATE_ADMIN_SCOPES_ERROR = '@event-template-admin/get-scopes-error';

export const GET_EVENT_TEMPLATE_ADMIN_LIST = '@event-template-admin/get-list';
export const GET_EVENT_TEMPLATE_ADMIN_LIST_SUCCESS = '@event-template-admin/get-list-success';
export const GET_EVENT_TEMPLATE_ADMIN_LIST_ERROR = '@event-template-admin/get-list-error';

export const UPSERT_EVENT_TEMPLATE_ADMIN = '@event-template-admin/upsert';
export const UPSERT_EVENT_TEMPLATE_ADMIN_SUCCESS = '@event-template-admin/upsert-success';
export const UPSERT_EVENT_TEMPLATE_ADMIN_ERROR = '@event-template-admin/upsert-error';

export const SET_EVENT_TEMPLATE_ADMIN_SCOPE = '@event-template-admin/set-scope';
export const CLEAR_EVENT_TEMPLATE_ADMIN = '@event-template-admin/clear';

// ── Helpers ──

const parseCompanyKey = (companyKey) => {
  if (!companyKey || companyKey === 'generic') return null;
  const match = /^company-(\d+)$/.exec(companyKey);
  return match ? Number(match[1]) : null;
};

// ── Action Creators ──

/** *************************************************************************
 * setEventTemplateAdminScope
 *
 * Records the currently-selected admin scope (companyKey + companyId).
 * Used so the detail screen knows which default row to edit + where to
 * return on back.
 *************************************************************************** */
export const setEventTemplateAdminScope = (companyKey) => ({
  type: SET_EVENT_TEMPLATE_ADMIN_SCOPE,
  payload: { companyKey: companyKey || null, companyId: parseCompanyKey(companyKey) },
});

/** *************************************************************************
 * getEventTemplateAdminScopes
 *
 * GET /api/eventTemplates/admin/scopes
 * Returns the query-driven set of default-scope options.
 *************************************************************************** */
export const getEventTemplateAdminScopes = () => async (dispatch) => {
  dispatch({ type: GET_EVENT_TEMPLATE_ADMIN_SCOPES });
  try {
    const response = await axios.get('/api/eventTemplates/admin/scopes');
    dispatch({
      type: GET_EVENT_TEMPLATE_ADMIN_SCOPES_SUCCESS,
      payload: { scopes: response?.data?.scopes || [] },
    });
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      GET_EVENT_TEMPLATE_ADMIN_SCOPES_ERROR,
      helpers.transform.toString(message || error || 'Could not load admin scopes'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: GET_EVENT_TEMPLATE_ADMIN_SCOPES_ERROR });
  }
};

/** *************************************************************************
 * getEventTemplateAdminList
 *
 * GET /api/eventTemplates/admin/list?companyId=N  (omit for generic)
 * Loads the default rows for a scope.
 *************************************************************************** */
export const getEventTemplateAdminList = (companyKey) => async (dispatch) => {
  dispatch({
    type: GET_EVENT_TEMPLATE_ADMIN_LIST,
    payload: { companyKey: companyKey || null, companyId: parseCompanyKey(companyKey) },
  });
  try {
    const companyId = parseCompanyKey(companyKey);
    const url = companyId
      ? `/api/eventTemplates/admin/list?companyId=${companyId}`
      : '/api/eventTemplates/admin/list';
    const response = await axios.get(url);
    dispatch({
      type: GET_EVENT_TEMPLATE_ADMIN_LIST_SUCCESS,
      payload: {
        companyKey: companyKey || null,
        companyId,
        results: response?.data?.results || [],
      },
    });
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      GET_EVENT_TEMPLATE_ADMIN_LIST_ERROR,
      helpers.transform.toString(message || error || 'Could not load default templates'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: GET_EVENT_TEMPLATE_ADMIN_LIST_ERROR });
  }
};

/** *************************************************************************
 * upsertEventTemplateAdmin
 *
 * PUT /api/eventTemplates/admin/:id
 *
 * `id` is either an existing default-row id (numeric) or the string "new"
 * when materializing a previously-missing default row. When "new", the
 * request body must supply templateType + companyKey.
 *************************************************************************** */
export const upsertEventTemplateAdmin = (
  {
    id,
    templateType,
    companyKey,
    subject,
    message,
    attachment,
    attachmentContainer,
  },
  doWhenTry = () => {},
  doWhenCatch = () => {},
) => async (dispatch) => {
  dispatch({ type: UPSERT_EVENT_TEMPLATE_ADMIN });
  try {
    const body = {
      subject,
      message,
      attachment,
      attachmentContainer,
    };
    // For "new" row materialization, backend needs templateType + companyId.
    if (!id || id === 'new') {
      body.templateType = templateType;
      body.companyId = parseCompanyKey(companyKey);
    }
    const response = await axios.put(`/api/eventTemplates/admin/${id || 'new'}`, body);
    dispatch({
      type: UPSERT_EVENT_TEMPLATE_ADMIN_SUCCESS,
      payload: response?.data || {},
    });
    dispatch(addNotification(
      UPSERT_EVENT_TEMPLATE_ADMIN_SUCCESS,
      'Default template saved',
      NOTIFICATION_STATUS.SUCCESS,
    ));
    doWhenTry(response?.data || {});
  } catch (e) {
    const { error: errMsg, message: respMessage } = e?.response?.data || {};
    dispatch(addNotification(
      UPSERT_EVENT_TEMPLATE_ADMIN_ERROR,
      helpers.transform.toString(respMessage || errMsg || 'Could not save default template'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: UPSERT_EVENT_TEMPLATE_ADMIN_ERROR });
    doWhenCatch(e);
  }
};

export const clearEventTemplateAdmin = () => ({ type: CLEAR_EVENT_TEMPLATE_ADMIN });
